import cloneDeep from 'lodash.clonedeep';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { State as TasksState } from './tasks.state';
import * as Adapters from './tasks.adapters';
import {
  Assignee,
  CleanType,
  CleanVM,
  RealtimeRoomsListViewItem,
  RealtimeRoomsRoomAttendant,
  RoomCleanTask,
  RoomCleanTaskDetailsVM,
  RoomCleanTaskStatus,
  RoomCleanTaskVM,
  Status,
  Task,
  TaskAssigneeVM,
  TaskDetailsVM,
  TaskFilter,
  TaskListVM,
  TaskVM,
} from '@app/core/async-services/http/versioned/task-management/task-management.interface';
import {
  formatDateTime,
  formatDateTimeWithWeekday,
  formatDateWithWeekDayShort,
} from '@app/core/utils/date/date.utils';
import { localeCompare } from '@app/shared/utils/array.utils';
import { selectUserId } from '@app/store/user/user.selectors';
import { User } from '@app/core/models';
import { selectQueryParams } from '@app/store/router/router.selectors';
import {
  getArrivalReadyTime,
  getRealtimeListViewSort,
  getFullCleanEndTimeRealtimeRooms,
  getRealtimeRoomsLatestEndOfCleaning,
  getTaskFilterFunction,
  getTaskNotesVM,
  isRoomCleanTask,
  roomAttendantViewSort,
  hasUnreadNote,
} from './tasks.util';
import {
  RealtimeRoomsSummary,
  WorkingStatus,
} from '@app/core/async-services/http/versioned/housekeeping/housekeeping.interface';
import moment from 'moment';

const getAssigneeStr = (assignee: Assignee) => {
  const unassigned = '(unassigned)';
  if (assignee.user) {
    return assignee.user.firstName + ' ' + assignee.user.lastName;
  } else if (assignee.teamName) {
    return `${assignee.teamName} ${unassigned}`;
  } else {
    return `${assignee.typeName} ${unassigned}`;
  }
};

export const selectFeature = createFeatureSelector<TasksState>('tasksState');

export const selectTasksUI = createSelector(
  selectFeature,
  (state) => state.tasksUI,
);

export const selectTasksMenu = createSelector(selectTasksUI, (ui) => ui.menu);

export const selectTasksHotel = createSelector(
  selectTasksMenu,
  (menu) => menu.hotel,
);

export const selectTasksFilter = createSelector(
  selectTasksMenu,
  (menu) => menu.filter,
);

export const selectEntId = createSelector(
  selectTasksHotel,
  (hotel) => hotel?.entId,
);

export const selectIsTaskListLoaded = createSelector(
  selectFeature,
  (state) => state.isTaskListLoaded,
);

export const selectTaskListState = createSelector(selectFeature, (state) => {
  return state.taskList;
});

export const selectNewTaskData = createSelector(
  selectFeature,
  (state) => state?.newTaskData,
);

export const selectTaskList = createSelector(
  selectTaskListState,
  Adapters.taskAdapter.getSelectors().selectAll,
);

export const selectTaskLocations = createSelector(
  selectNewTaskData,
  (newTaskData) => newTaskData?.locations,
);

export const selectTaskTypes = createSelector(
  selectNewTaskData,
  (newTaskData) => newTaskData?.types,
);

export const selectSettings = createSelector(
  selectNewTaskData,
  (newTaskData) => newTaskData?.settings,
);

export const selectIsRoomTypeHidden = createSelector(
  selectSettings,
  (settings) => settings?.isRoomTypeHidden,
);

export const selectCleanTypes = createSelector(
  selectNewTaskData,
  (newTaskData) => newTaskData?.cleanTypes,
);

export const selectIsNewTaskDataLoaded = createSelector(
  selectFeature,
  (state) => state.isNewTaskDataLoaded,
);

export const selectTaskAssignees = createSelector(
  selectFeature,
  (state) => state.assignees,
);

export const selectRealtimeTaskListSort = createSelector(
  selectFeature,
  (taskState) => taskState.realtimeRoomsTaskListSort,
);

export const selectRealtimeRoomsView = createSelector(
  selectFeature,
  (state) => state.realtimeRoomsView,
);

export const selectTaskAssigneesVM = createSelector(
  selectTaskAssignees,
  (taskAssignees) => {
    const taskAssigneesVM: TaskAssigneeVM[] = [];
    taskAssignees?.forEach((taskAssignee) => {
      const hasAssigneeList = !!taskAssignee.assigneeList?.length;
      taskAssigneesVM.push({
        disabled: hasAssigneeList,
        id: !hasAssigneeList ? taskAssignee.assigneeTypeId : null,
        level: 0,
        name: taskAssignee.assigneeTypeName,
        typeId: taskAssignee.assigneeTypeId,
      });
      if (hasAssigneeList) {
        const assigneeList = [...(taskAssignee.assigneeList ?? [])]
          .sort(
            localeCompare('name', {
              numeric: true,
            }),
          )
          .map((assignee) => ({
            disabled: false,
            id: assignee.assigneeId,
            level: 1,
            name: assignee.name,
            typeId: taskAssignee.assigneeTypeId,
          }));
        taskAssigneesVM.push(...assigneeList);
      }
    });
    return taskAssigneesVM;
  },
);

export const selectRoomCleanTaskAssigneesVM = createSelector(
  selectNewTaskData,
  selectTaskAssigneesVM,
  (newTaskData, assigness) => {
    const assigneeTypeIds = newTaskData?.assignees
      .filter(
        (assignee) =>
          assignee.assigneeTypeName === 'Team Members' ||
          assignee.assigneeTypeName === 'Managers',
      )
      .map((assignee) => assignee.assigneeTypeId);
    return assigness.filter((assignee) =>
      assigneeTypeIds?.includes(assignee.typeId),
    );
  },
);

export const selectHasTaskAssignees = createSelector(
  selectTaskAssignees,
  (taskAssignees) => Boolean(taskAssignees?.length),
);

export const selectIsTaskDetailsLoaded = createSelector(
  selectFeature,
  (state) => state.isTaskDetailsLoaded && state.isNewTaskDataLoaded,
);

export const selectTaskListDictionary = createSelector(
  selectTaskListState,
  Adapters.taskAdapter.getSelectors().selectEntities,
);

export const selectActiveTaskId = createSelector(
  selectQueryParams,
  (params) => {
    const taskId: string = params?.taskId;
    return taskId;
  },
);

export const selectOpenTask = createSelector(
  selectTaskListDictionary,
  selectActiveTaskId,
  (taskDictionary, taskId) => taskDictionary[taskId],
);

export const selectOpenRoomCleanTask = createSelector(
  selectOpenTask,
  (openTask) => {
    if (openTask && isRoomCleanTask(openTask)) {
      return openTask;
    }
  },
);

export const selectOpenTaskRoomTypeId = createSelector(
  selectOpenRoomCleanTask,
  (task) => task?.details.roomTypeId,
);

export const selectRoomStatuses = createSelector(
  selectNewTaskData,
  (newTaskData) => newTaskData?.roomStatuses ?? [],
);

export const selectTypeStatusCleanMaps = createSelector(
  selectNewTaskData,
  (newTaskData) => newTaskData?.typeStatusCleanMap ?? [],
);

export const selectTaskStatusList = createSelector(
  selectNewTaskData,
  (newTaskData) => newTaskData?.taskStatusList ?? [],
);

export const selectTaskStatusesDropdown = createSelector(
  selectTaskStatusList,
  (taskStatusList) =>
    taskStatusList
      .filter((status) => !status.name.startsWith('Auto Closed'))
      .map((status) => ({
        id: status.statusId,
        name: status.name,
      })),
);

export const selectRoomStatusesDropdown = createSelector(
  selectTypeStatusCleanMaps,
  selectRoomStatuses,
  selectOpenTaskRoomTypeId,
  (typeStatusCleanMaps, roomStatuses, roomTypeId) =>
    typeStatusCleanMaps
      .filter((item) => item.roomTypeId === roomTypeId)
      .map((item) =>
        roomStatuses.find(
          (roomStatus) => roomStatus.roomStatusId === item.roomStatusId,
        ),
      )
      .map((roomStatus) => ({
        id: roomStatus?.roomStatusId,
        name: roomStatus?.roomStatusName,
      })),
);

export const selectTaskDetailsVM = createSelector(
  selectOpenTask,
  selectUserId,
  (task, userId) => {
    const clonedTask: Task | undefined = cloneDeep(task);
    if (clonedTask?.typeName === 'Room Clean') {
      const roomCleanTaskDetails: RoomCleanTaskDetailsVM = {
        arrivalDate: formatDateWithWeekDayShort(
          clonedTask.details.arrivalDate!,
        ),
        assignee: clonedTask.assignee,
        assigneeName: getAssigneeStr(clonedTask.assignee),
        canMarkAsDone:
          clonedTask.statusName === Status.IN_PROGRESS &&
          clonedTask.assignee.user?.userId === userId,
        canStartProgress:
          clonedTask.statusName === Status.OPEN &&
          clonedTask.assignee.user?.userId === userId,
        changeLinen: clonedTask.details.changeLinen ?? false,
        cleanTypeName: clonedTask.details.cleanTypeName,
        createdOn: clonedTask.createdOn,
        createdUserFullName: clonedTask.createdUserFullName!,
        departureDate: formatDateWithWeekDayShort(
          clonedTask.details.departureDate!,
        ),
        description: clonedTask.description!,
        floor: clonedTask.details.floor,
        formattedCreatedOn: formatDateTimeWithWeekday(clonedTask.createdOn),
        guestCount: clonedTask.details.guestCount ?? '',
        guestRequested: clonedTask.guestRequested,
        id: clonedTask.id,
        isAssignedToCurrentUser:
          clonedTask.assignee.typeName === 'Team Members' &&
          clonedTask.assignee.user?.userId === userId,
        locationName: clonedTask.locationName,
        modifiedOn: formatDateTimeWithWeekday(clonedTask.modifiedOn),
        modifiedUserFullName: clonedTask.modifiedUserFullName!,
        reward: clonedTask.details.reward,
        roomNumber: clonedTask.details.roomNumber,
        roomStatusName: clonedTask.details.roomStatusName,
        roomStatusId: clonedTask.details.roomStatusId,
        roomTypeName: clonedTask.details.roomTypeName,
        sortOrder: clonedTask.details.sortOrder,
        statusId: clonedTask.statusId,
        statusName: clonedTask.statusName,
        targetMinutes: clonedTask.details.targetMinutes,
        title: clonedTask.title,
        titleHeader: `${clonedTask.details.roomNumber} (${clonedTask.statusName})`,
        typeId: clonedTask.typeId,
        typeName: clonedTask.typeName,
        notes:
          task?.notes.length === 0
            ? []
            : getTaskNotesVM(cloneDeep(task?.notes) ?? []),
        dndMarkedAtTimes: clonedTask.taskHistory
          .filter(
            (status) =>
              status.changeType === 'Clean Status' && status.newValue === 'DND',
          )
          .sort((a, b) => moment(a.modifiedOn).diff(b.modifiedOn))
          .map((status) => moment(status.modifiedOn).format('LT')),
      };
      return roomCleanTaskDetails;
    } else if (clonedTask) {
      const defaultTaskDetailsVM: TaskDetailsVM = {
        assignee: clonedTask.assignee,
        assigneeName: getAssigneeStr(clonedTask.assignee),
        canMarkAsDone:
          clonedTask.statusName === Status.IN_PROGRESS &&
          clonedTask.assignee.user?.userId === userId,
        canStartProgress:
          clonedTask.statusName === Status.OPEN &&
          clonedTask.assignee.user?.userId === userId,
        createdOn: clonedTask.createdOn,
        createdUserFullName: clonedTask.createdUserFullName!,
        description: clonedTask.description!,
        formattedCreatedOn: formatDateTimeWithWeekday(clonedTask.createdOn),
        guestRequested: clonedTask.guestRequested,
        id: clonedTask.id,
        isAssignedToCurrentUser:
          clonedTask.assignee.typeName === 'Team Members' &&
          clonedTask.assignee.user?.userId === userId,
        locationName: clonedTask.locationName,
        modifiedOn: formatDateTimeWithWeekday(clonedTask.modifiedOn),
        modifiedUserFullName: clonedTask.modifiedUserFullName!,
        statusId: clonedTask.statusId,
        statusName: clonedTask.statusName,
        title: clonedTask.title,
        titleHeader: `Task #${String(clonedTask.taskNumber)} (${
          clonedTask.statusName
        })`,
        typeId: clonedTask.typeId,
        typeName: clonedTask.typeName,
        notes:
          task?.notes.length === 0
            ? []
            : getTaskNotesVM(cloneDeep(task?.notes) ?? []),
      };
      return defaultTaskDetailsVM;
    }
  },
);

export const selectCleanVM = (roomStatusId: string) =>
  createSelector(
    selectTypeStatusCleanMaps,
    selectOpenTaskRoomTypeId,
    (typeStatusCleanMaps, roomTypeId) => {
      const typeStatusCleanMap = typeStatusCleanMaps.find(
        (item) =>
          item.roomTypeId === roomTypeId && item.roomStatusId === roomStatusId,
      );
      const cleanVM: CleanVM = {
        cleanType: typeStatusCleanMap?.name!,
        targetMinutes: typeStatusCleanMap?.mpr!,
      };
      return cleanVM;
    },
  );

export const selectStatusList = createSelector(
  selectNewTaskData,
  (data) => data?.taskStatusList,
);

export const selectStatusId = (statusName: RoomCleanTaskStatus | Status) =>
  createSelector(
    selectStatusList,
    (statusList) =>
      statusList?.find((status) => status.name === statusName)?.statusId,
  );

export const selectUpdateTaskStatusBody = (
  newStatus: RoomCleanTaskStatus | Status,
) =>
  createSelector(
    selectEntId,
    selectActiveTaskId,
    selectStatusId(newStatus),
    (entId, taskId, taskStatusId) => {
      return { taskId, entId, taskStatusId };
    },
  );

export const selectCreatedByUserId = createSelector(
  selectOpenTask,
  (task) => task?.createdByUserId,
);

export const selectIsTaskCreatedBySelf = createSelector(
  selectCreatedByUserId,
  selectUserId,
  (createdByUserId, userId) => createdByUserId === userId,
);

export const selectTaskStatusName = createSelector(
  selectOpenTask,
  (task) => task?.statusName,
);

export const selectTaskAssigneeUser = createSelector(
  selectOpenTask,
  (task) => task?.assignee.user,
);

export const selectIsTaskAssignedToSelf = createSelector(
  selectTaskAssigneeUser,
  selectUserId,
  (assignee, userId) =>
    assignee?.userType.toLowerCase() === User.Type.MANAGER.toLowerCase() &&
    assignee?.userId === userId,
);

export const selectTaskListVM = createSelector(
  selectTaskList,
  selectTasksFilter,
  selectUserId,
  selectTasksHotel,

  (taskList, taskFilter, userId, hotel): TaskListVM => {
    const filterTask = getTaskFilterFunction(taskFilter!);
    const filteredTaskList = taskList
      .filter((task) => {
        //TO DO: REMOVE THIS FILTER ONCE NULL DETAIL ISSUE FIXED
        if (task.typeName === 'Room Clean') {
          if (task.details === null) {
            return false;
          }
        }
        return true;
      })
      .filter((task) => task.entId === hotel?.entId)
      .map<TaskVM>((task) => {
        if (task.typeName === 'Room Clean') {
          const roomClean: RoomCleanTaskVM = {
            assignee: task.assignee,
            assigneeName: getAssigneeStr(task.assignee),
            changeLinen: task.details.changeLinen,
            cleanTypeName: task.details.cleanTypeName,
            createdOn: task.createdOn,
            formattedCreatedOn: formatDateTime(task.createdOn),
            guestRequested: task.guestRequested,
            id: task.id,
            isAssignedToCurrentUser:
              task.assignee.typeName === 'Managers' &&
              task.assignee.user.userId === userId,
            locationName: task.locationName,
            modifiedOn: task.modifiedOn,
            roomNumber: task.details.roomNumber,
            roomStatusName: task.details.roomStatusName,
            roomTypeName: task.details.roomTypeName,
            sortOrder: task.details.sortOrder,
            statusId: task.statusId,
            statusName: task.statusName as RoomCleanTaskStatus,
            title: task.title,
            typeId: task.typeId,
            typeName: task.typeName,
            targetMinutes: task.details.targetMinutes,
            notes:
              task.notes.length === 0
                ? []
                : getTaskNotesVM(cloneDeep(task.notes)),
            hasUnreadNote: hasUnreadNote(task.notes),
          };
          return roomClean;
        } else {
          return {
            assignee: task.assignee,
            assigneeName: getAssigneeStr(task.assignee),
            createdOn: task.createdOn,
            formattedCreatedOn: formatDateTime(task.createdOn),
            guestRequested: task.guestRequested,
            id: task.id,
            isAssignedToCurrentUser:
              task.assignee.typeName === 'Managers' &&
              task.assignee.user.userId === userId,
            locationName: task.locationName,
            modifiedOn: task.modifiedOn,
            statusId: task.statusId,
            statusName: task.statusName,
            title: task.title,
            typeId: task.typeId,
            typeName: task.typeName,
            notes: task.notes,
          };
        }
      })
      .filter(filterTask);
    if (
      taskFilter === TaskFilter.MY_RECENTLY_FINISHED_ROOM_CLEAN_TASKS ||
      taskFilter === TaskFilter.MY_UNFINISHED_ROOM_CLEAN_TASKS
    ) {
      return filteredTaskList.sort((a: TaskVM, b: TaskVM) => {
        if (a.typeName === 'Room Clean' && b.typeName === 'Room Clean') {
          if (a.sortOrder === b.sortOrder) {
            if (a.roomNumber > b.roomNumber) {
              return 1;
            } else if (a.roomNumber < b.roomNumber) {
              return -1;
            } else {
              return 0;
            }
          } else {
            return a.sortOrder - b.sortOrder;
          }
        } else {
          return 0;
        }
      });
    } else {
      return filteredTaskList;
    }
  },
);

export const selectTaskListVMLength = createSelector(
  selectTaskListVM,
  (taskList) => taskList.length,
);

export const selectHasTaskListVM = createSelector(
  selectTaskListVMLength,
  (length) => Boolean(length),
);

export const selectRoomCleanTaskListVM = createSelector(
  selectTaskListVM,
  (taskList): RoomCleanTaskVM[] =>
    taskList.filter(
      (task): task is RoomCleanTaskVM => task.typeName === 'Room Clean',
    ),
);

const selectRealtimeRoomsUI = createSelector(
  selectFeature,
  (state) => state.realtimeRoomsUI,
);

export const selectRealtimeRoomsMenu = createSelector(
  selectRealtimeRoomsUI,
  (ui) => ui.menu,
);

export const selectRealtimeRoomsHotel = createSelector(
  selectRealtimeRoomsMenu,
  (menu) => menu.hotel,
);

export const selectRealtimeRoomsEntId = createSelector(
  selectRealtimeRoomsHotel,
  (hotel) => hotel?.entId,
);

export const selectRealtimeRoomsUpdateTaskStatusBody = (
  newStatus: RoomCleanTaskStatus,
  isTaskRoute: boolean,
) =>
  createSelector(
    selectRealtimeRoomsEntId,
    selectEntId,
    selectActiveTaskId,
    selectStatusId(newStatus),
    (realtimeEntId, tasksEntId, taskId, taskStatusId) => {
      const entId = isTaskRoute ? tasksEntId : realtimeEntId;
      return { taskId, entId, taskStatusId };
    },
  );

export const selectIsRealtimeRoomsPageReady = createSelector(
  selectFeature,
  (state) =>
    state.isGetRoomCleanTaskListLoaded &&
    state.isGetRealtimeRoomsStatusSuccessful &&
    state.isNewTaskDataLoaded,
);

export const selectRealtimeRoomsErrorMessage = createSelector(
  selectFeature,
  (state) =>
    state.isGetRoomCleanTaskListSuccessful === false
      ? 'Room Cleans Failed To Load'
      : state.isGetRealtimeRoomsStatusSuccessful === false
      ? 'Summary Failed To Load'
      : '',
);

export const selectRoomCleanTasks = createSelector(selectTaskList, (taskList) =>
  taskList
    .filter(isRoomCleanTask)
    .sort((task1, task2) => task1.details.sortOrder - task2.details.sortOrder)
    .sort(localeCompare('title')),
);

export const selectRealtimeRoomsTasks = createSelector(
  selectRoomCleanTasks,
  selectRealtimeRoomsHotel,
  (tasks, hotel) => tasks.filter((task) => task.entId === hotel?.entId),
);

export const selectRealtimeRoomsListView = createSelector(
  selectRealtimeRoomsTasks,
  selectRealtimeTaskListSort,
  selectRealtimeRoomsView,
  (tasks, taskListSort, view): RealtimeRoomsListViewItem[] => {
    const sortFunction =
      taskListSort?.column !== null &&
      taskListSort?.orderBy !== null &&
      view === 'list'
        ? getRealtimeListViewSort(taskListSort!)
        : roomAttendantViewSort;
    return tasks
      .map<RealtimeRoomsListViewItem>((task) => ({
        taskId: task.id,
        roomNumber: task.details.roomNumber,
        roomType: task.details.roomTypeName,
        pmsStatus: task.details.roomStatusName,
        cleanType: task.details.cleanTypeName,
        changeLinen: task.details.changeLinen ?? false,
        assignee: task.title,
        cleanStatus: task.statusName as RoomCleanTaskStatus,
        isPaperOnly: task.assignee.isPaperOnly!,
        sortOrder: task.details.sortOrder,
        notes: task.notes,
        hasUnreadNote: hasUnreadNote(task.notes),
      }))
      .sort(sortFunction);
  },
);

export const selectRealtimeRoomsStatus = createSelector(
  selectFeature,
  (state) => state.realtimeRoomsStatus,
);

export const selectWorkingStatuses = createSelector(
  selectRealtimeRoomsStatus,
  (status) => status?.workingStatuses || [],
);

export const selectWorkingStatusMap = createSelector(
  selectWorkingStatuses,
  (workingStatuses) => {
    const workingStatusMap: { [id: string]: string } = {};
    workingStatuses.forEach(({ workingStatusId, workingStatusName }) => {
      workingStatusMap[workingStatusId] = workingStatusName;
    });
    return workingStatusMap;
  },
);

export const selectRealtimeRoomsAssignees = createSelector(
  selectRealtimeRoomsStatus,
  (status) => status?.assignees || [],
);

export const selectAssigneeWorkingStatuses = createSelector(
  selectRealtimeRoomsAssignees,
  selectWorkingStatusMap,
  (assignees, workingStatusMap) =>
    assignees.map((assignee) => ({
      id: assignee.assigneeId,
      type: assignee.assigneeType === 'MHTV2' ? 'Team Members' : 'Managers',
      workingStatus: workingStatusMap[
        assignee.workingStatusId!
      ] as WorkingStatus,
    })),
);

export const selectRealtimeRoomsRoomAttendantView = createSelector(
  selectRealtimeRoomsTasks,
  selectAssigneeWorkingStatuses,
  (tasks, assigneeWorkingStatuses): RealtimeRoomsRoomAttendant[] => {
    const attendantsMap = new Map<string, RealtimeRoomsRoomAttendant>();
    tasks.forEach((task) => {
      const key = task.title;
      if (attendantsMap.has(key)) {
        const attendant = attendantsMap.get(key);
        if (attendant !== undefined) {
          attendant.roomCleansDone += Number(task.statusName === 'Done');
          attendant.totalRoomCleans++;
        }
      } else {
        attendantsMap.set(task.title, {
          name: task.title,
          roomCleansDone: Number(task.statusName === 'Done'),
          totalRoomCleans: 1,
          workingStatus: assigneeWorkingStatuses.find(
            (assigneeWorkingStatus) =>
              assigneeWorkingStatus.id === task.assignee.id &&
              assigneeWorkingStatus.type === task.assignee.typeName,
          )?.workingStatus,
          isPaperOnly: task.assignee.isPaperOnly!,
        });
      }
    });
    const assignees = Array.from(attendantsMap.values());
    return assignees.sort(localeCompare('name'));
  },
);

export const selectRoomCleanSummary = createSelector(
  selectFeature,
  (state) => state.roomCleanSummary,
);

export const selectCleanTypeDictionary = createSelector(
  selectCleanTypes,
  (cleanTypes) => {
    const cleanTypeDictionary: { [cleanTypeId: string]: CleanType } = {};
    cleanTypes?.forEach((cleanType) => {
      cleanTypeDictionary[cleanType.cleanTypeId] = cleanType;
    });
    return cleanTypeDictionary;
  },
);

export const selectAssigneeRoomCleanTaskDictionary = createSelector(
  selectRealtimeRoomsTasks,
  (tasks) => {
    const dictionary: { [uniqueAssigneeId: string]: RoomCleanTask[] } = {};
    tasks.forEach((task) => {
      const key =
        task.assignee.typeName === 'Write In'
          ? task.assignee.typeId + task.assignee.id + task.title
          : task.assignee.typeId + task.assignee.id;
      if (dictionary[key]) {
        dictionary[key].push(task);
      } else {
        dictionary[key] = [task];
      }
    });
    return dictionary;
  },
);

export const selectRealtimeRoomsSummary = (currentTime: string) =>
  createSelector(
    selectAssigneeRoomCleanTaskDictionary,
    selectRoomCleanSummary,
    selectSettings,
    selectCleanTypeDictionary,
    selectTaskListDictionary,
    (
      assigneeRoomCleanTaskDictionary,
      summary,
      settings,
      cleanTypeDictionary,
      taskDictionary,
    ) => {
      const { todayEstimatedArrivals, minimumFullCleansForArrivals } = summary!;
      const allAssignedCleans = Object.values(
        assigneeRoomCleanTaskDictionary,
      ).flatMap((tasks) => tasks.map((task) => taskDictionary[task.id]));
      const assignedFullCleans = allAssignedCleans.filter(
        (task): task is RoomCleanTask =>
          task !== undefined &&
          'details' in task &&
          cleanTypeDictionary[task.details.cleanTypeId]?.metaType === 'full',
      );
      const filteredTaskDictionary: { [taskId: string]: Task } = {};
      for (const [key, value] of Object.entries(taskDictionary)) {
        if (value !== undefined) {
          filteredTaskDictionary[key] = value;
        }
      }
      const fullCleanEndTimes = getFullCleanEndTimeRealtimeRooms({
        assigneeDictionary: assigneeRoomCleanTaskDictionary,
        taskDictionary: filteredTaskDictionary,
        settings,
        cleanTypeDictionary,
        currentTime,
      });

      const lastCleanMoment = getRealtimeRoomsLatestEndOfCleaning(
        assigneeRoomCleanTaskDictionary,
        settings,
        currentTime,
        cleanTypeDictionary,
      );
      let lastCleanOfDay: string;
      let isLastCleanLate: boolean;
      if (lastCleanMoment === 'No Room Cleans Published') {
        lastCleanOfDay = 'No Room Cleans Published';
        isLastCleanLate = false;
      } else {
        lastCleanOfDay = lastCleanMoment.format('h:mm A');
        isLastCleanLate = lastCleanMoment.isSameOrAfter(
          moment('17:00:00', 'HH:mm:ss'),
          'hour',
        );
      }
      const individualsCleaningRooms = Object.keys(
        assigneeRoomCleanTaskDictionary,
      ).length;
      const remainingTotalFullCleans = assignedFullCleans.filter(
        (task) => task?.statusName !== Status.DONE,
      ).length;
      const doneFullCleans =
        assignedFullCleans.length - remainingTotalFullCleans;
      const remainingFullCleansNeededForArrivals =
        minimumFullCleansForArrivals - doneFullCleans <= 0
          ? 0
          : minimumFullCleansForArrivals - doneFullCleans;
      const remainingOtherCleans = allAssignedCleans.filter(
        (task): task is RoomCleanTask =>
          task !== undefined &&
          'details' in task &&
          cleanTypeDictionary[task.details.cleanTypeId].metaType !== 'full' &&
          task.statusName !== Status.DONE,
      ).length;
      const { arrivalReadyTime, isReadyTimeTooLate } = getArrivalReadyTime(
        assignedFullCleans.length,
        fullCleanEndTimes,
        remainingFullCleansNeededForArrivals,
        todayEstimatedArrivals,
      );
      const realTimeSummary: RealtimeRoomsSummary = {
        arrivalReadyTime,
        isReadyTimeTooLate,
        lastCleanOfDay,
        isLastCleanLate,
        individualsCleaningRooms,
        remainingFullCleansNeededForArrivals,
        remainingTotalFullCleans,
        remainingOtherCleans,
      };
      return realTimeSummary;
    },
  );

export const selectOpenTaskRoomNumber = createSelector(selectOpenTask, (task) =>
  task?.typeName === 'Room Clean'
    ? task?.details?.roomNumber
    : String(task?.taskNumber),
);

export const selectOpenTaskNotes = createSelector(
  selectTaskDetailsVM,
  (task) => {
    return task?.notes;
  },
);
